import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Briefcase, MapPin, Wallet, Users, Check, X, Send, ChevronDown, CreditCard, Sparkles, Lock, Utensils, Gift, Banknote } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { euro, fmt } from "../lib/format";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { LiveMap } from "../components/LiveMap";
import { DeliveryDialog } from "../components/DeliveryDialog";
import { ContractDialog } from "../components/ContractDialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";

const CATS = ["Food", "Lifestyle", "Mode", "Beauté", "Voyage", "Café", "Fitness", "Gastronomie"];
const REWARD = {
  remuneration: { icon: Wallet, key: "rewardPay" },
  meal: { icon: Utensils, key: "rewardMeal" },
  product: { icon: Gift, key: "rewardProduct" },
  other: { icon: Sparkles, key: "rewardOther" },
};
const statusColor = {
  accepted: "text-emerald-400 bg-emerald-400/10",
  rejected: "text-red-400 bg-red-400/10",
  pending: "text-amber-400 bg-amber-400/10",
  paid: "text-emerald-400 bg-emerald-400/10",
};

function CreateCampaign({ onCreated }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "Food", budget: 300, city: "Lyon", reward_type: "remuneration", reward_value: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/campaigns", { ...form, budget: Number(form.budget) });
      toast.success(t("createCampaign"));
      onCreated(data.campaign);
      setOpen(false);
      setForm({ title: "", description: "", category: "Food", budget: 300, city: "Lyon", reward_type: "remuneration", reward_value: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="open-create-campaign" className="flex items-center gap-2 rounded-full bg-gold px-4 py-2.5 text-sm font-bold text-ink">
          <Plus size={16} /> {t("createCampaign")}
        </button>
      </DialogTrigger>
      <DialogContent className="border-cardline bg-card2 text-white">
        <DialogHeader><DialogTitle className="font-heading">{t("newCampaign")}</DialogTitle>
          <DialogDescription className="text-slate-400">{t("createCampaign")}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <input data-testid="campaign-title-input" placeholder={t("title")} value={form.title} onChange={set("title")}
            className="w-full rounded-xl border border-cardline bg-ink2/60 px-4 py-3 text-sm outline-none focus:border-gold" />
          <textarea data-testid="campaign-desc-input" placeholder={t("description")} value={form.description} onChange={set("description")} rows={3}
            className="w-full rounded-xl border border-cardline bg-ink2/60 px-4 py-3 text-sm outline-none focus:border-gold" />
          <div className="grid grid-cols-2 gap-2">
            <div className="relative">
              <select data-testid="campaign-cat-select" value={form.category} onChange={set("category")}
                className="w-full appearance-none rounded-xl border border-cardline bg-ink2/60 px-4 py-3 text-sm outline-none focus:border-gold">
                {CATS.map((c) => <option key={c} value={c} className="bg-ink2">{c}</option>)}
              </select>
              <ChevronDown size={15} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
            </div>
            <input type="number" data-testid="campaign-budget-input" placeholder={t("budget")} value={form.budget} onChange={set("budget")}
              className="w-full rounded-xl border border-cardline bg-ink2/60 px-4 py-3 text-sm outline-none focus:border-gold" />
          </div>
          <input data-testid="campaign-city-input" placeholder={t("city")} value={form.city} onChange={set("city")}
            className="w-full rounded-xl border border-cardline bg-ink2/60 px-4 py-3 text-sm outline-none focus:border-gold" />
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-400">{t("rewardType")}</label>
            <div className="grid grid-cols-2 gap-2">
              <div className="relative">
                <select data-testid="campaign-reward-select" value={form.reward_type} onChange={set("reward_type")}
                  className="w-full appearance-none rounded-xl border border-cardline bg-ink2/60 px-4 py-3 text-sm outline-none focus:border-gold">
                  <option value="remuneration" className="bg-ink2">{t("rewardPay")}</option>
                  <option value="meal" className="bg-ink2">{t("rewardMeal")}</option>
                  <option value="product" className="bg-ink2">{t("rewardProduct")}</option>
                  <option value="other" className="bg-ink2">{t("rewardOther")}</option>
                </select>
                <ChevronDown size={15} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>
              <input data-testid="campaign-reward-value" placeholder={t("rewardValuePh")} value={form.reward_value} onChange={set("reward_value")}
                className="w-full rounded-xl border border-cardline bg-ink2/60 px-4 py-3 text-sm outline-none focus:border-gold" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <button onClick={submit} disabled={loading || !form.title} data-testid="submit-campaign-btn"
            className="w-full rounded-xl bg-gold py-3 font-bold text-ink disabled:opacity-60">
            {loading ? t("loading") : t("createCampaign")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ApplyDialog({ campaign, onApplied }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try {
      await api.post(`/campaigns/${campaign.id}/apply`, { campaign_id: campaign.id, message: msg });
      toast.success(t("applied"));
      onApplied();
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`apply-btn-${campaign.id}`} className="flex items-center gap-1.5 rounded-full bg-gold px-4 py-2 text-sm font-bold text-ink">
          <Send size={14} /> {t("apply")}
        </button>
      </DialogTrigger>
      <DialogContent className="border-cardline bg-card2 text-white">
        <DialogHeader><DialogTitle className="font-heading">{campaign.title}</DialogTitle>
          <DialogDescription className="text-slate-400">{campaign.business_name}</DialogDescription></DialogHeader>
        <textarea data-testid="apply-message-input" rows={4} placeholder={t("applyMessage")} value={msg} onChange={(e) => setMsg(e.target.value)}
          className="w-full rounded-xl border border-cardline bg-ink2/60 px-4 py-3 text-sm outline-none focus:border-gold" />
        <DialogFooter>
          <button onClick={submit} disabled={loading} data-testid="submit-apply-btn" className="w-full rounded-xl bg-gold py-3 font-bold text-ink disabled:opacity-60">
            {loading ? t("loading") : t("apply")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SubscribeBanner({ price }) {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const subscribe = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/subscription/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setLoading(false);
    }
  };
  return (
    <div data-testid="subscribe-banner" className="flex flex-col gap-3 rounded-2xl border border-fuchsia-400/40 bg-fuchsia-500/10 p-4 sm:flex-row sm:items-center">
      <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-fuchsia-500/20 text-fuchsia-300"><Lock size={22} /></div>
      <div className="flex-1">
        <div className="font-heading font-bold text-fuchsia-200">{t("premiumCreator")}</div>
        <div className="text-xs text-slate-300">{t("subscribeDesc")}</div>
      </div>
      <button onClick={subscribe} disabled={loading} data-testid="subscribe-btn"
        className="flex items-center justify-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-ink disabled:opacity-60">
        <Sparkles size={15} /> {t("subscribe")}{price ? ` · ${euro(price)}${t("perMonth")}` : ""}
      </button>
    </div>
  );
}

export default function Campaigns() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [price, setPrice] = useState(null);
  const [connect, setConnect] = useState(null);
  const isCommerce = user?.role === "commerce";
  const isCreator = user?.role === "creator";
  const subscribed = !!user?.subscription_active;

  const onboard = async () => {
    try {
      const { data } = await api.post("/connect/onboard", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const load = useCallback(() => {
    api.get("/campaigns", { params: isCommerce ? { mine: true } : {} })
      .then(({ data }) => setItems(data.campaigns)).catch(() => {});
  }, [isCommerce]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isCreator && !subscribed) api.get("/subscription/config").then(({ data }) => setPrice(data.amount)).catch(() => {});
  }, [isCreator, subscribed]);
  useEffect(() => {
    if (isCreator) api.get("/connect/status").then(({ data }) => setConnect(data)).catch(() => {});
  }, [isCreator]);

  const decide = async (cid, creatorId, status) => {
    await api.post(`/campaigns/${cid}/proposals/${creatorId}/status`, null, { params: { status } });
    toast.success(status === "accepted" ? t("accepted") : t("rejected"));
    load();
  };

  const pay = async (campaign, p) => {
    try {
      const { data } = await api.post("/payments/checkout", {
        campaign_id: campaign.id, creator_id: p.creator_id, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const myProposal = (c) => c.proposals?.find((p) => p.creator_id === user?.id);

  return (
    <div className="space-y-5 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-extrabold">{isCommerce ? t("myCampaigns") : t("openCampaigns")}</h1>
          <p className="mt-1 text-sm text-slate-400">{t("campaigns")}</p>
        </div>
        {isCommerce && <CreateCampaign onCreated={() => load()} />}
      </div>

      {isCreator && !subscribed && <SubscribeBanner price={price} />}

      {isCreator && connect && !connect.ready && items.some((c) => { const p = myProposal(c); return p && ["accepted", "paid"].includes(p.status); }) && (
        <div data-testid="connect-card" className="flex flex-col gap-3 rounded-2xl border border-sky-400/40 bg-sky-500/10 p-4 sm:flex-row sm:items-center">
          <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-sky-500/20 text-sky-300"><Banknote size={22} /></div>
          <div className="flex-1"><div className="font-heading font-bold text-sky-200">{t("activatePayouts")}</div><div className="text-xs text-slate-300">{t("payoutsDesc")}</div></div>
          <button onClick={onboard} data-testid="connect-onboard-btn" className="rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-ink">{t("activatePayouts")}</button>
        </div>
      )}

      {items.some((c) => c.lat && c.lng) && (
        <div>
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-300">
            <MapPin size={15} className="text-gold" /> {t("missionsOnMap")}
          </div>
          <LiveMap missions={items} height="15rem" />
        </div>
      )}

      {items.length === 0 && <p className="py-16 text-center text-sm text-slate-500">{t("noCampaigns")}</p>}

      <div className="space-y-3">
        {items.map((c) => {
          const mine = myProposal(c);
          return (
          <div key={c.id} data-testid={`campaign-${c.id}`} className="rounded-2xl border border-cardline bg-card2/60 p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="grid h-11 w-11 place-items-center rounded-xl bg-gold/15 text-gold"><Briefcase size={20} /></div>
                <div>
                  <div className="font-heading font-semibold">{c.title}</div>
                  <div className="text-xs text-slate-400">{c.business_name}</div>
                </div>
              </div>
              <span className="rounded-full bg-emerald-400/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-400">{c.status}</span>
            </div>
            <p className="mt-3 text-sm text-slate-300">{c.description}</p>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-400">
              <span className="rounded-full bg-ink2/70 px-2.5 py-1">{c.category}</span>
              <span className="flex items-center gap-1"><Wallet size={13} className="text-gold" /> {euro(c.budget)}</span>
              {(() => { const r = REWARD[c.reward_type] || REWARD.remuneration; const I = r.icon; return (
                <span data-testid={`reward-${c.id}`} className="flex items-center gap-1 rounded-full bg-gold/10 px-2 py-1 font-semibold text-gold">
                  <I size={12} /> {t(r.key)}{c.reward_value ? ` · ${c.reward_value}` : ""}
                </span>); })()}
              <span className="flex items-center gap-1"><MapPin size={13} /> {c.city}</span>
              <span className="flex items-center gap-1"><Users size={13} /> {c.proposals?.length || 0} {t("proposals")}</span>
            </div>

            {!isCommerce && (
              <div className="mt-3 flex items-center justify-end gap-2">
                {mine && ["accepted", "paid"].includes(mine.status) && (
                  <ContractDialog campaignId={c.id} triggerTestId={`contract-open-${c.id}`}
                    triggerClassName="flex items-center gap-1.5 rounded-full border border-cardline bg-ink2/60 px-3.5 py-2 text-sm font-semibold text-slate-200 hover:text-gold" />
                )}
                {mine && ["accepted", "paid"].includes(mine.status) && (
                  <DeliveryDialog campaignId={c.id} campaignTitle={c.title} role="creator"
                    triggerTestId={`delivery-open-${c.id}`}
                    triggerClassName="flex items-center gap-1.5 rounded-full border border-cardline bg-ink2/60 px-3.5 py-2 text-sm font-semibold text-slate-200 hover:text-gold" />
                )}
                {mine
                  ? <span className={`rounded-full px-3 py-1.5 text-sm font-semibold ${statusColor[mine.status] || "bg-emerald-400/10 text-emerald-400"}`}>{t(mine.status) || t("applied")}</span>
                  : (subscribed
                      ? <ApplyDialog campaign={c} onApplied={load} />
                      : <span data-testid={`locked-${c.id}`} className="flex items-center gap-1.5 rounded-full border border-fuchsia-400/40 px-3 py-1.5 text-xs font-semibold text-fuchsia-300"><Lock size={12} /> {t("subscribeToApply")}</span>)}
              </div>
            )}

            {isCommerce && c.proposals?.length > 0 && (
              <div className="mt-3 space-y-2 border-t border-cardline pt-3">
                {c.proposals.map((p) => (
                  <div key={p.creator_id} data-testid={`proposal-${c.id}-${p.creator_id}`} className="flex items-center gap-3 rounded-xl bg-ink2/50 p-2.5">
                    <img src={p.creator_avatar} alt={p.creator_name} className="h-9 w-9 rounded-full object-cover" />
                    <div className="flex-1">
                      <div className="text-sm font-semibold">{p.creator_name}</div>
                      <div className="text-[11px] text-slate-400">{fmt(p.followers)} · {p.engagement}%</div>
                    </div>
                    {["accepted", "paid"].includes(p.status) && (
                      <DeliveryDialog campaignId={c.id} campaignTitle={c.title} role="commerce"
                        triggerTestId={`delivery-open-${c.id}-${p.creator_id}`}
                        triggerClassName="flex items-center gap-1 rounded-full border border-cardline bg-ink2 px-2.5 py-1.5 text-[11px] font-semibold text-slate-200 hover:text-gold" />
                    )}
                    {p.status === "pending" ? (
                      <div className="flex gap-1.5">
                        <button onClick={() => decide(c.id, p.creator_id, "accepted")} data-testid={`accept-${c.id}-${p.creator_id}`}
                          className="grid h-8 w-8 place-items-center rounded-lg bg-emerald-500/20 text-emerald-400"><Check size={16} /></button>
                        <button onClick={() => decide(c.id, p.creator_id, "rejected")} data-testid={`reject-${c.id}-${p.creator_id}`}
                          className="grid h-8 w-8 place-items-center rounded-lg bg-red-500/20 text-red-400"><X size={16} /></button>
                      </div>
                    ) : p.status === "accepted" ? (
                      <button onClick={() => pay(c, p)} data-testid={`pay-${c.id}-${p.creator_id}`}
                        className="flex items-center gap-1.5 rounded-full bg-gold px-3 py-1.5 text-xs font-bold text-ink">
                        <CreditCard size={13} /> {t("payShort")}
                      </button>
                    ) : (
                      <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${statusColor[p.status] || "text-slate-300 bg-ink2/70"}`}>{t(p.status) || p.status}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
}
